import { useEffect, useRef, type ReactNode } from 'react';
import QRCode from 'qrcode';
import type { Kind } from '../lib/types';
import { KIND_LABEL } from '../lib/format';

export const LOGO_SRC = '/one10-logo.png';

export function Chip({ tone = '', children }: { tone?: '' | 'ok' | 'warn' | 'bad'; children: ReactNode }) {
  return <span className={`chip ${tone}`}>{children}</span>;
}

export function VegMark({ kind }: { kind: Kind }) {
  return <span className={`vm vm-${kind}`} aria-hidden="true" />;
}

export function KindLabel({ kind }: { kind: Kind }) {
  return <><VegMark kind={kind} />{KIND_LABEL[kind]}</>;
}

export function Steps({ steps }: { steps: { label: string; state: '' | 'done' | 'bad' }[] }) {
  return (
    <ol className="msteps">
      {steps.map((s) => <li key={s.label} className={s.state}>{s.label}</li>)}
    </ol>
  );
}

export function Stepper({ value, max, onChange, label, disabled }: {
  value: number; max: number; onChange: (v: number) => void; label: string; disabled?: boolean;
}) {
  return (
    <div className="stepper">
      <button type="button" aria-label={`One fewer ${label}`} disabled={disabled || value <= 0} onClick={() => onChange(value - 1)}>−</button>
      <output aria-live="polite" aria-label={label}>{value}</output>
      <button type="button" aria-label={`One more ${label}`} disabled={disabled || value >= max} onClick={() => onChange(value + 1)}>+</button>
    </div>
  );
}

export function Spinner() {
  return <span className="spin" role="status" aria-label="Loading" />;
}

export function Splash({ text }: { text?: string }) {
  return <div className="splash"><div className="stack center" style={{ justifyItems: 'center' }}><Spinner />{text && <p>{text}</p>}</div></div>;
}

export function QrCanvas({ value, size = 480, className }: { value: string; size?: number; className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    QRCode.toCanvas(ref.current, value, {
      width: size, margin: 2, errorCorrectionLevel: 'M',
      color: { dark: '#1f140c', light: '#ffffff' },
    }).catch(() => { /* value too long: not expected for our payloads */ });
  }, [value, size]);
  return <canvas ref={ref} className={className} role="img" aria-label="QR code" />;
}

/** One dot per seat: served (hollow), kept for a guest (dashed), then free (filled). */
export function Pips({ total, used, given = 0, locked }: { total: number; used: number; given?: number; locked?: boolean }) {
  const cls = (i: number) => (locked ? '' : i < used ? 'used' : i < used + given ? 'given' : '');
  return (
    <span className={`pips${locked ? ' locked' : ''}`} aria-hidden="true">
      {Array.from({ length: total }, (_, i) => <i key={i} className={cls(i)} />)}
    </span>
  );
}

export function ErrorNote({ error }: { error: string | null | undefined }) {
  return error ? <p className="err" role="alert">{error}</p> : null;
}
